import { MealCategoriesService } from '@/backend/market'
import { MealCategoryRepository } from '../../domain/repositories/MealCategoryRepository'
import type { MealCategoryFilterEntity } from '../../domain/entities/MealCategoryFilterEntity'
import { MealCategoryFilterModel } from '../models/MealCategoryFilterModel'
import { MealCategoryMiniModel } from '../models/MealCategoryMiniModel'
import { PaginateEntity } from '@/shared/entities/PaginateEntity'
import { MealCategoryModel } from '../models/MealCategoryModel'
import type { MealCategoryEntity } from '../../domain/entities/MealCategoryEntity'
import type { CreateUpdateMealCategoryEntity } from '../../domain/entities/CreateUpdateMealCategoryEntity'
import { CrateUpdateMealCategoryModel } from '../models/CrateUpdateMealCategoryModel'
import type { MealCategoryItemFilterEntity } from '../../domain/entities/MealCategoryItemFilterEntity'
import type { UpdateMealCategoryItemEntity } from '../../domain/entities/UpdateMealCategoryItemEntity'
import { UpdateMealCategoryItemModel } from '../models/UpdateMealCategoryItemModel'
import type { MealCategoryTierFilterEntity } from '../../domain/entities/MealCategoryTierFilterEntity'
import type { UpdateMealCategoryTierEntity } from '../../domain/entities/UpdateMealCategoryTierEntity'
import { UpdateMealCategoryTierModel } from '../models/UpdateMealCategoryTierModel'

export class MealCategoryRepositoryImpl implements MealCategoryRepository {
  async getMealCategories(
    filterData: MealCategoryFilterEntity
  ): Promise<PaginateEntity<MealCategoryMiniModel[]> | { error: true }> {
    const mealCategoryFilterModel = new MealCategoryFilterModel(filterData)
    try {
      const response = await MealCategoriesService.getMealCategoriesAdmin(mealCategoryFilterModel)
      const mealCategoriesMiniEntity = response.items?.map(
        (item) => new MealCategoryMiniModel(item)
      )
      const mealCategoryResponse = new PaginateEntity<MealCategoryMiniModel[]>(
        response,
        mealCategoriesMiniEntity
      )

      return mealCategoryResponse
    } catch {
      return { error: true }
    }
  }
  async getMealCategory(id: number): Promise<MealCategoryEntity | { error: true }> {
    try {
      const response = await MealCategoriesService.getMealCategoryAdmin({
        id
      })
      const mealCategoryEntity = new MealCategoryModel(response)
      return mealCategoryEntity
    } catch {
      return { error: true }
    }
  }
  async getMealCategoryItems(
    filterData: MealCategoryItemFilterEntity
  ): Promise<
    | PaginateEntity<{ id?: number; name?: string | null; imageUrl?: string | null }>
    | { error: true }
  > {
    try {
      const response = await MealCategoriesService.getMealCategoryItemsAdmin(filterData)
      return response
    } catch {
      return { error: true }
    }
  }
  async updateMealCategoryItems(
    id: number,
    updateData: UpdateMealCategoryItemEntity
  ): Promise<{ success: true } | { error: true }> {
    try {
      const updateMealCategoryItemModel = new UpdateMealCategoryItemModel(updateData)
      await MealCategoriesService.putMealCategoryItemsAdmin({
        id,
        requestBody: updateMealCategoryItemModel
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async getMealCategoryTiers(
    filterData: MealCategoryTierFilterEntity
  ): Promise<PaginateEntity<{ id?: number; name?: string | null }> | { error: true }> {
    try {
      const response = await MealCategoriesService.getMealCategoryMealTiersAdmin(filterData)
      return response
    } catch {
      return { error: true }
    }
  }
  async updateMealCategoryTiers(
    id: number,
    updateData: UpdateMealCategoryTierEntity
  ): Promise<{ success: true } | { error: true }> {
    try {
      const updateMealCategoryTierModel = new UpdateMealCategoryTierModel(updateData)
      await MealCategoriesService.putMealCategoryMealTiersAdmin({
        id,
        requestBody: updateMealCategoryTierModel
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async createMealCategory(
    mealCategoryData: CreateUpdateMealCategoryEntity
  ): Promise<{ success: true } | { error: true }> {
    const createUpdateMealCategoryModel = new CrateUpdateMealCategoryModel(mealCategoryData)
    try {
      await MealCategoriesService.postMealCategoryAdmin({
        requestBody: createUpdateMealCategoryModel
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async updateMealCategory(
    id: number,
    mealCategoryData: CreateUpdateMealCategoryEntity
  ): Promise<{ success: true } | { error: true }> {
    const createUpdateMealCategoryModel = new CrateUpdateMealCategoryModel(mealCategoryData)
    try {
      await MealCategoriesService.putMealCategoryAdmin({
        id,
        requestBody: createUpdateMealCategoryModel
      })
      return { success: true }
    } catch (error) {
      return { error: true }
    }
  }
  async updateMealCategoryOrder(
    id: number,
    order: number
  ): Promise<{ success: true } | { error: true }> {
    try {
      await MealCategoriesService.putMealCategoryOrderAdmin({
        id,
        requestBody: {
          order
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async deleteMealCategory(id: number): Promise<{ success: true } | { error: true }> {
    try {
      await MealCategoriesService.deleteMealCategoryAdmin({
        id
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
}
